import { checkIsBusinessUser } from "selectors/user";
import {
  checkSelectedBusinessHasBusinessUsers,
  checkSelectedBusinessRpaMode,
  getCurrentQuickBooksRealmId,
  getSelectedBusinessBookCloseDate,
  getSelectedBusinessData,
  getSelectedBusinessId,
  getSelectedBusinessIntegrationServiceData,
  getTransactionsInProgressStats
} from "selectors/businesses";
import { checkTransactionsFetching, getTransactionsData } from "selectors/transactions";
import { getActiveOrganization } from "selectors/organizations";
import { getTextsData } from "selectors/texts";
import { toast } from "react-toastify";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Arrays from "utils/Arrays";
import Async from "utils/Async";
import BusinessesActions from "actions/BusinessesActions";
import CommentTargetTypes from "const/CommentTargetTypes";
import CommentTypes from "const/CommentTypes";
import CommentsActions from "actions/CommentsActions";
import Constants from "const/Constants";
import DataStatuses from "const/DataStatuses";
import DateTimeFormats from "const/DateTimeFormats";
import IntegrationServices from "const/IntegrationServices";
import LocalStorageKeys from "const/LocalStorageKeys";
import MongoDb from "utils/MongoDb";
import Objects from "utils/Objects";
import OrganizationsActions from "actions/OrganizationsActions";
import RoboticActions from "actions/RoboticActions";
import RpaWorkflows from "const/RpaWorkflows";
import Storage from "utils/Storage";
import Texts from "utils/Texts";
import TransactionFieldsToEdit from "const/TransactionFieldsToEdit";
import Transactions from "utils/Transactions";
import TransactionsActions from "actions/TransactionsActions";
import TransactionsStatuses from "nlib/pages/TransactionsPage/TransactionsStatuses";
import UiActions from "actions/UiActions";
import UserRoles from "const/UserRoles";
import moment from "moment";
import useEnvVars from "hooks/useEnvVars";
import useShowCommonModal from "./useShowCommonModal";
import useTransactionUtils from "./useTransactionUtils";

const { QUICK_BOOKS } = IntegrationServices;

const INITIAL_STATE = { transactionsState: [], transactionsData: [] };

const checkAskClientAllowed = (status, quickBooksBusiness) => {
  return [
    DataStatuses.TO_RECONCILE,
    DataStatuses.NEED_DOCUMENT,
    DataStatuses.NEED_REACTION,
    DataStatuses.TO_REVIEW
  ].includes(status) || (status === DataStatuses.EXPORTED && quickBooksBusiness);
};

const getTransactionChanges = (prevState, prevData, nextData) => {
  const changes = Object.values(TransactionFieldsToEdit).map((fieldName) => {
    const prevStateValue = prevState[fieldName];

    const prevDataValue = prevData[fieldName];

    const nextDataValue = nextData[fieldName];

    switch (fieldName) {
      case TransactionFieldsToEdit.ITEM:
      case TransactionFieldsToEdit.CLASS:
      case TransactionFieldsToEdit.LOCATION:
      case TransactionFieldsToEdit.PROJECT:
      case TransactionFieldsToEdit.TAX_RATE:
        return (prevDataValue?.name === nextDataValue?.name) || (!prevDataValue?.name && prevStateValue?.name)
          ? false
          : [fieldName, nextDataValue];
      case TransactionFieldsToEdit.TAGS:
        return (prevDataValue?.join() === nextDataValue?.join()) || (!prevDataValue?.join() && prevStateValue?.join())
          ? false
          : [fieldName, nextDataValue];
      case TransactionFieldsToEdit.ADDRESS:
      case TransactionFieldsToEdit.VENDOR_ID:
        return (prevData.vendorId === nextData.vendorId) || (!prevData.vendorId && prevState.vendorId)
          ? false
          : [fieldName, nextDataValue];
      case TransactionFieldsToEdit.REASON:
        return false;
      default:
        return (prevDataValue === nextDataValue) || (!prevDataValue && prevStateValue)
          ? false
          : [fieldName, nextDataValue];
    }
  }).filter(Boolean);

  return changes.length ? Object.fromEntries(changes) : null;
};

const updateTransactionsState = ({
  data,
  prevData,
  prevState,
  rpaExcludedWords,
  lastGptAiFineTuneStatus
}) => {
  return data
    ? data.map((nextTransactionData) => {
      const prevTransactionState = Arrays.findById(prevState, nextTransactionData.id);

      const prevTransactionData = Arrays.findById(prevData, nextTransactionData.id, {});

      if (!prevTransactionState) {
        return Transactions.getTransactionState(nextTransactionData, { lastGptAiFineTuneStatus, rpaExcludedWords });
      }

      const changes = getTransactionChanges(prevTransactionState, prevTransactionData, nextTransactionData);

      return changes ? { ...prevTransactionState, ...changes } : prevTransactionState;
    })
    : prevState;
};

const useTransactions = ({ defaultStatus } = {}) => {
  const dispatch = useDispatch();

  const [envVars, setEnvVars] = useEnvVars();

  const { uiTexts, messages, errors } = useSelector(getTextsData);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const selectedBusinessBookCloseDate = useSelector(getSelectedBusinessBookCloseDate);

  const businessUser = useSelector(checkIsBusinessUser);

  const rpaMode = useSelector(checkSelectedBusinessRpaMode);

  const selectedBusinessIntegrationServiceData = useSelector(getSelectedBusinessIntegrationServiceData);

  const {
    byRoboticAiStatus: {
      awaitingReconciliation: {
        count: awaitingReconciliationCount = 0
      } = {}
    } = {}
  } = useSelector(getTransactionsInProgressStats) || {};

  const {
    id: businessId,
    settings: { advancedDocumentsWorkflow, rpaExcludedWords } = {},
    extraData: {
      integrationService,
      lastGptAiFineTuneStatus,
      transactionsLastSyncedAt,
      integrationServiceConnected,
      lastManualRpaWorkflowStartedAt
    } = {}
  } = useSelector(getSelectedBusinessData);

  const quickBooksService = integrationService === QUICK_BOOKS.value;

  const selectedBusinessId = useSelector(getSelectedBusinessId);

  const selectedBusinessHasBusinessUsers = useSelector(checkSelectedBusinessHasBusinessUsers);

  const { businessOrganization } = useSelector(getActiveOrganization);

  const { checkIsReadyToReview, checkIsReadyToApprove } = useTransactionUtils();

  const showCommonModal = useShowCommonModal();

  const [selectedTransactions, setSelectedTransactions] = useState([]);

  const [fetching, setFetching] = useState(false);

  const [initialFetchDone, setInitialFetchDone] = useState(false);

  const { text, editItem } = envVars;

  const [searchTransactionId] = (editItem || "").split(".");

  const searchByMongoId = MongoDb.checkIsMongoId(text);

  let {
    pageSize = Constants.TABLE_PAGE_SIZE,
    page = 1,
    sortBy,
    sortOrder,
    month,
    status = (!searchByMongoId && businessUser)
      ? DataStatuses.NEED_REACTION
      : defaultStatus,
    fromDate,
    toDate,
    accountId,
    type
  } = envVars;

  if (month) {
    const momentDate = moment(month);

    if (momentDate.isValid()) {
      fromDate = momentDate.startOf("month").format(DateTimeFormats.API_DATE);
      toDate = momentDate.endOf("month").format(DateTimeFormats.API_DATE);
    }
  }

  const transactionsData = useSelector(getTransactionsData);

  const transactionsFetching = useSelector(checkTransactionsFetching);

  const editItemFoundInData = useMemo(() => {
    return transactionsData.some(({ id }) => id === searchTransactionId);
  }, [transactionsData, searchTransactionId]);

  const transactionIdToEnvVars = !text && !editItemFoundInData && searchTransactionId;

  const [{ transactionsState }, setState] = useState(INITIAL_STATE);

  const setTransactionsState = useCallback((newState) => {
    setState((prev) => {
      const updatedState = typeof newState === "function"
        ? newState(prev.transactionsState) : newState;

      return { ...prev, transactionsState: updatedState };
    });
  }, []);

  const hasFilters = !![text, fromDate, toDate, accountId, type].find(Boolean);

  const fetchData = useCallback(async(clearList = false, backgroundUpdate = false) => {
    const result = await dispatch(TransactionsActions.fetchTransactionsList(
      searchByMongoId ? undefined : status,
      {
        text,
        fromDate,
        toDate,
        accountId,
        type
      },
      sortBy ? { id: sortBy, desc: sortOrder === "desc" } : null,
      (page - 1) * pageSize,
      +pageSize,
      clearList,
      backgroundUpdate
    ));

    return result;
  }, [
    dispatch,
    page,
    text,
    type,
    status,
    sortBy,
    toDate,
    fromDate,
    pageSize,
    sortOrder,
    accountId,
    searchByMongoId
  ]);

  const refetchTransactions = useCallback(async(silently) => {
    const result = await fetchData(false, silently);

    setState((prevState) => {
      return {
        transactionsData: result,
        transactionsState: updateTransactionsState({
          data: result,
          prevState: prevState.transactionsState,
          prevData: prevState.transactionsData,
          rpaExcludedWords,
          lastGptAiFineTuneStatus
        })
      };
    });
  }, [lastGptAiFineTuneStatus, rpaExcludedWords, fetchData]);

  const selectedTransactionsData = useMemo(() => {
    return transactionsData.filter(({ id }) => selectedTransactions.includes(id));
  }, [selectedTransactions, transactionsData]);

  const closedBookDateTransactionIds = useMemo(() => {
    if (!selectedBusinessBookCloseDate || !transactionsData) return [];

    return transactionsData.filter(({ timestamp }) => {
      return moment(timestamp).isSameOrBefore(moment(selectedBusinessBookCloseDate))
        && (quickBooksBusiness || status !== DataStatuses.EXPORTED);
    }).map(({ id }) => id);
  }, [selectedBusinessBookCloseDate, quickBooksBusiness, status, transactionsData]);

  const editableTransactionsIds = useMemo(() => {
    if (envVars.status === DataStatuses.IN_PROGRESS) return [];

    return transactionsData.filter(({ status: transactionStatus, aiProcessing }) => {
      if (businessUser) return transactionStatus === DataStatuses.NEED_REACTION;

      return !aiProcessing
        && transactionStatus !== DataStatuses.TO_REPORT
        && (quickBooksBusiness || transactionStatus !== DataStatuses.EXPORTED);
    }).map(({ id }) => id);
  }, [envVars.status, transactionsData, businessUser, quickBooksBusiness]);

  const transactionsReadyToProcess = useMemo(() => {
    if (!rpaMode || businessUser) return [];

    return transactionsState
      .filter((item) => {
        const transactionData = Arrays.findById(transactionsData, item.id);

        return editableTransactionsIds.includes(item.id)
          && transactionData?.status === DataStatuses.NEED_REACTION && !item.manualMode && !!item.reason?.trim();
      });
  }, [rpaMode, businessUser, transactionsState, transactionsData, editableTransactionsIds]);

  const transactionsReadyToReview = useMemo(() => {
    return transactionsState
      .filter((item) => {
        const transactionData = Arrays.findById(transactionsData, item.id);

        return transactionData && checkIsReadyToReview(item, transactionData);
      });
  }, [transactionsData, transactionsState, checkIsReadyToReview]);

  const transactionsReadyToApprove = useMemo(() => {
    if (!integrationServiceConnected) return [];

    return transactionsState
      .filter((transactionState) => {
        const transactionData = Arrays.findById(transactionsData, transactionState.id);

        if (!transactionData) return null;

        return checkIsReadyToApprove(transactionState, transactionData);
      });
  }, [integrationServiceConnected, transactionsData, transactionsState, checkIsReadyToApprove]);

  const selectedNeedReactionIds = useMemo(() => {
    if (businessUser) return [];

    return selectedTransactionsData.filter((transaction) => {
      const currentStatus = envVars.status || Arrays.findById(transactionsData, transaction.id, {}).status;

      return checkAskClientAllowed(currentStatus, quickBooksBusiness);
    }).map(({ id }) => id);
  }, [businessUser, quickBooksBusiness, selectedTransactionsData, envVars.status, transactionsData]);

  const [popupMode, transactionsForPopup] = useMemo(() => {
    if (selectedNeedReactionIds.length && (!businessOrganization || selectedBusinessHasBusinessUsers)) {
      return [null, []];
    }
    if (envVars.status === DataStatuses.IN_PROGRESS) {
      if (!transactionsLastSyncedAt || !awaitingReconciliationCount) return [];

      return [DataStatuses.IN_PROGRESS, new Array(awaitingReconciliationCount)];
    }
    if (transactionsReadyToApprove.length) {
      return [DataStatuses.TO_REPORT, transactionsReadyToApprove.filter(({ id }) => !closedBookDateTransactionIds.includes(id))];
    }
    if (transactionsReadyToProcess.length) {
      return [DataStatuses.TO_REVIEW, transactionsReadyToProcess.filter(({ id }) => !closedBookDateTransactionIds.includes(id))];
    }
    if (transactionsReadyToReview.length) {
      return [DataStatuses.TO_REVIEW, transactionsReadyToReview.filter(({ id }) => !closedBookDateTransactionIds.includes(id))];
    }

    return [null, []];
  }, [
    envVars.status,
    awaitingReconciliationCount,
    transactionsReadyToApprove,
    transactionsReadyToProcess,
    transactionsReadyToReview,
    transactionsLastSyncedAt,
    selectedNeedReactionIds.length,
    businessOrganization,
    selectedBusinessHasBusinessUsers,
    closedBookDateTransactionIds
  ]);

  const startRpa = useCallback(async() => {
    const [
      manualRpaRunDescriptionA,
      manualRpaRunDescriptionB,
      manualRpaRunDescriptionC
    ] = messages.manualRpaRunDescription;

    const manualRpaStartTimeLeft = lastManualRpaWorkflowStartedAt
      ? Math.max(
        moment.utc(lastManualRpaWorkflowStartedAt)
          .add(Constants.MANUAL_RPA_START_COOLDOWN, "milliseconds").diff(moment.utc()),
        0
      )
      : 0;

    const modalResult = await showCommonModal({
      text: [
        manualRpaRunDescriptionA,
        manualRpaStartTimeLeft
          ? Texts.replaceVars(
            manualRpaRunDescriptionB,
            { cooldownTime: moment.duration(manualRpaStartTimeLeft).humanize(true) }
          )
          : manualRpaRunDescriptionC
      ].join("\n\n"),
      headerText: manualRpaStartTimeLeft ? uiTexts.notification : uiTexts.confirm,
      confirm: !manualRpaStartTimeLeft,
      okButtonText: manualRpaStartTimeLeft ? uiTexts.ok : uiTexts.continue
    });

    if (modalResult && !manualRpaStartTimeLeft) {
      await dispatch(RoboticActions.startRpaWorkflow(
        quickBooksService ? RpaWorkflows.MATCH_TRANSACTIONS : RpaWorkflows.RECONCILE_TRANSACTONS
      ));
      await dispatch(BusinessesActions.fetchBusiness(businessId, true));
    }
  }, [
    businessId,
    dispatch,
    quickBooksService,
    lastManualRpaWorkflowStartedAt,
    showCommonModal,
    uiTexts,
    messages
  ]);

  const askClient = useCallback(async(userEmail, userPhone = "", comment) => {
    const savedEmailLsKey = `${LocalStorageKeys.LAST_ASKED_BUSINESS_EMAIL}_${selectedBusinessId}`;

    const successful = await dispatch(OrganizationsActions.askBusinessUser(
      selectedNeedReactionIds, userEmail, userPhone, comment
    ));

    if (successful) Storage.storageValue(savedEmailLsKey, `${userEmail}:${moment().unix()}`);
    dispatch(OrganizationsActions.fetchUsersList());
    refetchTransactions();
  }, [dispatch, refetchTransactions, selectedBusinessId, selectedNeedReactionIds]);

  const askNewClient = useCallback(async(comment) => {
    const result = await dispatch(UiActions.showAskClientWindow({
      transactionsCount: selectedNeedReactionIds.length
    }));

    if (result) {
      const { userEmail, userPhone, invite } = result;

      if (invite) {
        await dispatch(OrganizationsActions.inviteUser({
          email: userEmail,
          role: UserRoles.BUSINESS_MANAGER.roleId,
          businessIds: [selectedBusinessId],
          emailNotifications: true
        }));
      }
      await askClient(userEmail, userPhone, comment);
    }
  }, [askClient, dispatch, selectedBusinessId, selectedNeedReactionIds]);

  const onSelectedChange = useCallback((transactionId, value) => {
    setSelectedTransactions((prev) => value ? [...prev, transactionId] : prev.filter((el) => el !== transactionId));
  }, []);

  const onAskClientPopupClose = useCallback((result) => {
    setSelectedTransactions([]);
    if (result) {
      const { userEmail, userPhone, comment } = result;

      if (userEmail) askClient(userEmail, userPhone, comment);
      else askNewClient(comment);
    }
  }, [askClient, askNewClient, setSelectedTransactions]);

  const onBulkActionsEdit = useCallback(async(data) => {
    if (!businessUser && !Transactions.validateContactChange(data)) return;

    const {
      tags,
      reason,
      address,
      project,
      taxRate,
      category,
      location,
      vendorId,
      class: classValue,
      status: innerStatus,
      directCategorySelection
    } = data;

    if (selectedTransactionsData.some(({ id }) => closedBookDateTransactionIds.includes(id))) {
      const closedBookTimeText = moment.utc(selectedBusinessBookCloseDate).format(DateTimeFormats.DATE_TEXT_EXT);

      showCommonModal({
        text: Texts.replaceVars(messages.transactionsClosedBookWarning, {
          date: closedBookTimeText,
          service: selectedBusinessIntegrationServiceData.label
        }),
        headerText: uiTexts.warning
      });

      return;
    }
    setFetching(true);
    setSelectedTransactions([]);

    const allowedToEditTransactionsData = selectedTransactionsData.filter((item) => {
      return !closedBookDateTransactionIds.includes(item.id)
        && item.status !== DataStatuses.TO_REPORT && (item.status !== DataStatuses.EXPORTED || quickBooksBusiness);
    });

    if (allowedToEditTransactionsData.length) {
      if (innerStatus) {
        const ids = allowedToEditTransactionsData.filter((item) => {
          const { canBeAssigned } = TransactionsStatuses.getStatusData([innerStatus]);

          return canBeAssigned && item.status !== innerStatus;
        }).map(({ id }) => id);

        setState((prevState) => {
          return {
            ...prevState,
            transactionsState: prevState.transactionsState.map((item) => {
              return ids.includes(item.id) ? { ...item, status: innerStatus } : item;
            })
          };
        });
        await dispatch(TransactionsActions.bulkTransactionsUpdate({ ids, data: { status: innerStatus } }, null, true));
      } else {
        const fullUpdate = allowedToEditTransactionsData.map(({ id, documentId }) => {
          const updates = [
            innerStatus && { status: innerStatus },
            reason && { reason },
            ...((!advancedDocumentsWorkflow || !documentId) ? [
              vendorId && { address, vendorId },
              category && { category, directCategorySelection, tags },
              classValue && { class: classValue },
              location && { location },
              project && { project },
              taxRate && { taxRate }
            ] : [])
          ].filter(Boolean);

          return updates.length && updates.reduce((aggregator, update) => ({ ...aggregator, ...update }), { id });
        }).filter(Boolean);

        if (fullUpdate.length) {
          const {
            successCount = 0,
            failedTxs = []
          } = await dispatch(TransactionsActions.bulkTransactionsUpdate(null, fullUpdate, true)) || {};

          const failedTransactionIds = failedTxs.map(({ transactionId }) => transactionId);

          if (successCount > 0 && reason) {
            await Async.runInSequence(
              fullUpdate.filter(({ id }) => !failedTransactionIds.includes(id)).map(({ id }) => async() => {
                await dispatch(CommentsActions.addNewComment(
                  CommentTypes.COMMENT,
                  `${uiTexts.reason}: ${reason}`,
                  [],
                  true,
                  id,
                  CommentTargetTypes.TRANSACTIONS
                ));
              })
            );
          }
        }
        if (selectedTransactionsData.length === 1 && fullUpdate.length === 1) {
          toast.success(messages.transactionEdited);
        } else {
          toast.success(Texts.replaceVars(
            messages.transactionsChanged,
            { successCount: fullUpdate.length, totalCount: selectedTransactionsData.length }
          ));
        }
      }
      refetchTransactions();
    } else {
      toast.error(Texts.replaceVars(errors.whileChangingTransactions,
        { transactionsCount: selectedTransactionsData.length }));
    }
    setFetching(false);
  }, [
    businessUser,
    errors,
    uiTexts,
    messages,
    quickBooksBusiness,
    selectedTransactionsData,
    advancedDocumentsWorkflow,
    closedBookDateTransactionIds,
    selectedBusinessIntegrationServiceData,
    selectedBusinessBookCloseDate,
    showCommonModal,
    refetchTransactions,
    dispatch
  ]);

  const onBulkActionsCancel = useCallback(() => {
    setSelectedTransactions([]);
  }, []);

  const onTransactionsPopupSubmit = useCallback(async() => {
    if (envVars.status === DataStatuses.IN_PROGRESS) {
      startRpa();

      return;
    }

    const invalidContactChange = popupMode === DataStatuses.TO_REPORT && transactionsForPopup.some((state) => {
      const transaction = Arrays.findById(transactionsData, state.id);

      return !Transactions.validateContactChange(state, transaction);
    });

    if (invalidContactChange) return;

    const payload = transactionsForPopup
      .filter(({ id }) => !closedBookDateTransactionIds.includes(id))
      .map((transaction) => {
        return {
          id: transaction.id,
          ...Objects.getProps(transaction, Object.values(TransactionFieldsToEdit))
        };
      });

    await dispatch(TransactionsActions.bulkTransactionsUpdate(null, payload));
    refetchTransactions();
    await Async.runInSequence(payload.map(({ id, reason }) => async() => {
      if (reason) {
        await dispatch(CommentsActions.addNewComment(
          CommentTypes.COMMENT,
          `${uiTexts.reason}: ${reason}`,
          [],
          true,
          id,
          CommentTargetTypes.TRANSACTIONS
        ));
      }
    }));
  }, [
    envVars.status,
    popupMode,
    transactionsForPopup,
    transactionsData,
    closedBookDateTransactionIds,
    uiTexts,
    dispatch,
    refetchTransactions,
    startRpa
  ]);

  useEffect(() => {
    setState((prevState) => {
      const newState = updateTransactionsState({
        data: transactionsData,
        prevState: prevState.transactionsState,
        prevData: prevState.transactionsData,
        rpaExcludedWords,
        lastGptAiFineTuneStatus
      });

      return { ...prevState, transactionsState: newState };
    });
  }, [lastGptAiFineTuneStatus, businessUser, rpaExcludedWords, transactionsData]);
  useLayoutEffect(() => {
    let destroyed = false;

    setInitialFetchDone(false);
    if (transactionIdToEnvVars) {
      setEnvVars({ text: transactionIdToEnvVars });
    } else {
      (async() => {
        await fetchData();
        if (!destroyed) {
          setInitialFetchDone(true);
        }
      })();
    }

    const clearFetchInterval = Async.setInterval(
      () => fetchData(false, true), Constants.DATA_LIST_UPDATE_INTERVAL
    );

    return () => {
      destroyed = true;
      clearFetchInterval();
    };
  }, [fetchData, setEnvVars, transactionIdToEnvVars]);

  return {
    hasFilters,
    initialFetchDone,
    transactionsFetching,
    transactionsData,
    transactionsState,
    popupMode,
    fetching,
    transactionsForPopup,
    selectedTransactions,
    selectedTransactionsData,
    editableTransactionsIds,
    transactionsReadyToProcess,
    transactionsReadyToReview,
    transactionsReadyToApprove,
    selectedNeedReactionIds,
    closedBookDateTransactionIds,
    setTransactionsState,
    refetchTransactions,
    setFetching,
    setSelectedTransactions,
    onSelectedChange,
    onAskClientPopupClose,
    onBulkActionsEdit,
    onBulkActionsCancel,
    onTransactionsPopupSubmit
  };
};

export default useTransactions;
